import React from 'react';
import { Award, CheckCircle, Clock, Shield, Users } from 'lucide-react';
import { contactWhatsApp, scrollToSection } from '../utils/buttonActions';
import SectionHeader from './SectionHeader';
import IndustrialButton from './IndustrialButton';

const About = () => {
  const whyChooseUs = [
    { icon: <Award className="h-6 w-6" strokeWidth={2} />, title: '25+ YEARS', desc: 'Established in 1997 with deep signage and print production experience.' },
    { icon: <Users className="h-6 w-6" strokeWidth={2} />, title: 'EXPERT TEAM', desc: 'Design, fabrication, print, and install teams coordinated under one workflow.' },
    { icon: <Clock className="h-6 w-6" strokeWidth={2} />, title: 'ON TIME', desc: 'Clear deadlines, practical material advice, and quick-response project handling.' },
    { icon: <Shield className="h-6 w-6" strokeWidth={2} />, title: 'QUALITY', desc: 'Premium media, controlled finishing, and durable installation standards.' },
  ];

  const values = [
    'Quality without compromise',
    'Practical material guidance',
    'Transparent pricing',
    'Timely project delivery',
    'Professional installation',
    'Long-lasting finishes',
  ];

  return (
    <section id="about" className="py-20 lg:py-28 bg-[#F9F9F6] border-b-2 border-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeader
          eyebrow="Shop Floor Standard"
          title="Why Choose"
          accent="Aim Flex?"
          copy="A practical production team with design, fabrication, printing, and installation under one roof."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20" data-reveal>
          <div className="space-y-8">
            <div className="industrial-copy-panel">
              <span>Madurai / Est. 1997</span>
              <h3>Transforming rough signage ideas into ready-to-install work.</h3>
              <p>
                Since 1997, Aim Flex has helped local businesses, events, and retail spaces turn visibility problems into clear printed systems. We combine fast consultation with material knowledge, print production, frame work, and installation support.
              </p>
              <p>
                The result is simple: fewer surprises, stronger finishes, and signage that is readable from the distance it was designed for.
              </p>
            </div>

            <div>
              <h4 className="about-control-title text-sm font-black tracking-widest uppercase text-[#0A0A0A] mb-4">Control Checks</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {values.map((value) => (
                  <div key={value} className="about-check-row flex items-center space-x-3 group">
                    <CheckCircle className="h-4 w-4 text-[#FF2A00] flex-shrink-0" strokeWidth={2} />
                    <span className="about-check-text text-sm text-[#404040] group-hover:text-[#0A0A0A] transition-colors">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="about-stat-grid">
              <div>
                <strong>500+</strong>
                <span>Happy Clients</span>
              </div>
              <div>
                <strong>1000+</strong>
                <span>Projects</span>
              </div>
              <div>
                <strong>25+</strong>
                <span>Years</span>
              </div>
              <div>
                <strong>15+</strong>
                <span>Team</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <IndustrialButton onClick={() => contactWhatsApp('Hi! I would like to discuss my signage requirements.')} testId="about-contact-btn">
                Contact Us
              </IndustrialButton>
              <IndustrialButton onClick={() => scrollToSection('portfolio')} variant="light" testId="about-portfolio-btn">
                View Work
              </IndustrialButton>
            </div>
          </div>

          <div className="about-command-card">
            <div className="bg-[#0A0A0A] text-white p-8">
              <div className="w-16 h-16 bg-white flex items-center justify-center mb-6 p-2 border-2 border-[#FF2A00]">
                <img src="/aim-logo.png" alt="Aim Flex" className="h-full w-full object-contain" />
              </div>
              <h4>AIM FLEX</h4>
              <p>THE PRINTING PEOPLE / EST. 1997</p>
            </div>
            <div className="p-8">
              <div className="mission-box">
                <span>Mission</span>
                <p>"To provide the best service without compromising quality and time."</p>
              </div>
              <div className="about-spec-list">
                <div><span>Location</span><strong>Madurai, TN</strong></div>
                <div><span>Experience</span><strong>25+ Years</strong></div>
                <div><span>Services</span><strong>Print / Frame / Install</strong></div>
              </div>
            </div>
          </div>
        </div>

        <div className="about-card-grid" data-reveal>
          {whyChooseUs.map((item, index) => (
            <div key={item.title} className="about-feature-card" data-testid={`about-card-${index}`}>
              <div>{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
